import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, ToggleButton, Tooltip } from '@mui/material';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import haversine from 'haversine'; // ✅ OpenLayer wala exact haversine use kiya hai

// Markers
import busIcon from '../../../assets/marker_bus.png';
import stopMarkerIcon from '../../../assets/marker_stop.png';

const GoogleMap = ({ position, thingModel, selectedStopId, onStopSelect }) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const [mapReady, setMapReady] = useState(false);
    const [follow, setFollow] = useState(true);
    const markersRef = useRef({ bus: null, stops: [] });
    const polylinesRef = useRef({ covered: null, remain: null });
    const infoWindowRef = useRef(null);

    // Decode Polyline accurately
    const decodePolyline = (encoded) => {
        if (!encoded?.trim()) return [];
        const poly = [];
        let index = 0, len = encoded.length, lat = 0, lng = 0;
        try {
            while (index < len) {
                let b, shift = 0, result = 0;
                do {
                    b = encoded.charCodeAt(index++) - 63;
                    result |= (b & 0x1f) << shift;
                    shift += 5;
                } while (b >= 0x20);
                const dlat = result & 1 ? ~(result >> 1) : result >> 1;
                lat += dlat;

                shift = 0; result = 0;
                do {
                    b = encoded.charCodeAt(index++) - 63;
                    result |= (b & 0x1f) << shift;
                    shift += 5;
                } while (b >= 0x20);
                const dlng = result & 1 ? ~(result >> 1) : result >> 1;
                lng += dlng;

                poly.push({ lat: lat / 1e5, lng: lng / 1e5 });
            }
        } catch (e) {
            console.error('Polyline decode error:', e);
        }
        return poly;
    };

    const applyStyles = (element, styles) => {
        Object.entries(styles).forEach(([key, value]) => {
            element.style[key] = value;
        });
    };

    // Init Map
    const initMap = useCallback(() => {
        if (!window.google?.maps || !mapRef.current) return;

        const map = new window.google.maps.Map(mapRef.current, {
            zoom: 18,
            center: { lat: 20.5937, lng: 78.9629 },
            mapTypeId: 'roadmap',
            disableDefaultUI: true,
            zoomControl: false,
            gestureHandling: 'greedy',
            styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
        });

        mapInstance.current = map;
        infoWindowRef.current = new window.google.maps.InfoWindow();

        // ✅ Vertical Custom Zoom UI (Jaisa image me tha)
        const zoomControl = document.createElement('div');
        applyStyles(zoomControl, {
            display: 'flex',
            flexDirection: 'column',
            margin: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
            overflow: 'hidden'
        });

        const btnStyle = {
            width: '34px', height: '34px',
            border: 'none', background: 'transparent',
            fontSize: '22px', fontWeight: '500',
            cursor: 'pointer', color: '#333',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0
        };

        const zoomIn = document.createElement('button');
        applyStyles(zoomIn, { ...btnStyle, borderBottom: '1px solid #f0f0f0' });
        zoomIn.innerHTML = '+';

        const zoomOut = document.createElement('button');
        applyStyles(zoomOut, btnStyle);
        zoomOut.innerHTML = '−';

        zoomIn.onclick = () => map.setZoom(map.getZoom() + 1);
        zoomOut.onclick = () => map.setZoom(map.getZoom() - 1);

        [zoomIn, zoomOut].forEach((btn) => {
            btn.onmouseover = () => { btn.style.backgroundColor = '#f5f5f5'; };
            btn.onmouseout = () => { btn.style.backgroundColor = 'transparent'; };
        });

        zoomControl.appendChild(zoomIn);
        zoomControl.appendChild(zoomOut);
        map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(zoomControl);

        map.addListener('click', () => {
            infoWindowRef.current.close();
        });

        setMapReady(true);
    }, []);

    // Load API
    useEffect(() => {
        if (!window.google?.maps) {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDw39yxlkY6aDVDhqj-W0wBblVKt_mw7xI&libraries=geometry`;
            script.async = true;
            script.onload = () => initMap();
            document.head.appendChild(script);
            return () => { document.head.removeChild(script); };
        } else {
            initMap();
        }
    }, [initMap]);

    // Exact OpenLayer Haversine Logic
    const findClosestPointIndex = (busCoord, polyPoints) => {
        let minDistance = Infinity;
        let closestIndex = 0;
        polyPoints.forEach((point, index) => {
            const dist = haversine(
                { latitude: busCoord.latitude, longitude: busCoord.longitude },
                { latitude: point.lat, longitude: point.lng },
                { unit: 'meter' }
            );
            if (dist < minDistance) {
                minDistance = dist;
                closestIndex = index;
            }
        });
        return closestIndex;
    };

    // Main Update Loop
    useEffect(() => {
        if (!mapReady || !mapInstance.current || !position) return;

        const lat = Number(position.latitude ?? position.lat);
        const lng = Number(position.longitude ?? position.lng);
        if (isNaN(lat) || isNaN(lng)) return;

        const pos = { lat, lng };

        // === 1. Bus Marker ===
        if (!markersRef.current.bus) {
            markersRef.current.bus = new window.google.maps.Marker({
                map: mapInstance.current,
                icon: {
                    url: busIcon,
                    scaledSize: new window.google.maps.Size(48, 48),
                    anchor: new window.google.maps.Point(24, 48),
                },
                zIndex: 1500
            });
            mapInstance.current.setCenter(pos);
        }
        markersRef.current.bus.setPosition(pos);

        // === 2. Stop Markers ===
        const stops = thingModel.stops || {};
        const stopArray = Object.values(stops)
            .filter((s) => s.location?.latitude && s.location?.longitude)
            .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

        const existingMarkers = markersRef.current.stops.reduce((acc, marker) => {
            acc[marker.stopId] = marker;
            return acc;
        }, {});

        const newMarkers = [];

        stopArray.forEach((stop, i) => {
            const stopPos = { lat: Number(stop.location.latitude), lng: Number(stop.location.longitude) };
            const isSelected = selectedStopId === stop.id;
            let marker = existingMarkers[stop.id];

            if (!marker) {
                marker = new window.google.maps.Marker({
                    position: stopPos,
                    map: mapInstance.current,
                    icon: {
                        url: stopMarkerIcon,
                        scaledSize: new window.google.maps.Size(36, 36),
                        anchor: new window.google.maps.Point(18, 36),
                        labelOrigin: new window.google.maps.Point(18, 14),
                    },
                    label: {
                        text: `${i + 1}`, color: 'white', fontSize: '12px', fontWeight: 'bold',
                    },
                    zIndex: isSelected ? 1000 : 100,
                });

                marker.stopId = stop.id;
                marker.addListener('click', () => {
                    onStopSelect(stop.id);
                    infoWindowRef.current.setContent(`
                        <div style="padding:4px; font-size:14px; font-weight:bold; color:#333;">
                            ${stop.locationName}
                        </div>
                    `);
                    infoWindowRef.current.open(mapInstance.current, marker);
                });
            } else {
                marker.setPosition(stopPos);
                marker.setZIndex(isSelected ? 1000 : 100);
            }
            newMarkers.push(marker);
        });

        markersRef.current.stops.forEach((marker) => {
            if (!stopArray.find((s) => s.id === marker.stopId)) marker.setMap(null);
        });
        markersRef.current.stops = newMarkers;

        // === 3. Polyline Split Logic (Exact OpenLayers logic) ===
        const poly = thingModel.currentJourney?.wayPointsPolyLine;
        if (poly) {
            const pts = decodePolyline(poly);
            
            if (pts.length > 0) {
                const busLocationObject = { latitude: lat, longitude: lng };
                const closestIndex = findClosestPointIndex(busLocationObject, pts);

                // OpenLayer Slice exactly
                const coveredPoints = pts.slice(0, closestIndex + 1);
                coveredPoints.push(pos); 

                const remainingPoints = pts.slice(closestIndex);
                remainingPoints.unshift(pos);

                // Render Green Line
                if (polylinesRef.current.covered) polylinesRef.current.covered.setMap(null);
                polylinesRef.current.covered = new window.google.maps.Polyline({
                    path: coveredPoints,
                    strokeColor: '#2e7d32', 
                    strokeOpacity: 1,
                    strokeWeight: 6,
                    zIndex: 10,
                    map: mapInstance.current,
                });

                // Render Red Line
                if (polylinesRef.current.remain) polylinesRef.current.remain.setMap(null);
                polylinesRef.current.remain = new window.google.maps.Polyline({
                    path: remainingPoints,
                    strokeColor: '#f04c31', 
                    strokeOpacity: 1,
                    strokeWeight: 6,
                    zIndex: 999, // ✅ FIX: Forced to the extreme top so it never hides behind Green
                    map: mapInstance.current,
                });
            }
        } else {
            if (polylinesRef.current.covered) polylinesRef.current.covered.setMap(null);
            if (polylinesRef.current.remain) polylinesRef.current.remain.setMap(null);
        }

        // === 4. Follow Mode ===
        if (follow) {
            mapInstance.current.panTo(pos);
        }

    }, [mapReady, position, thingModel, selectedStopId, onStopSelect, follow]);

    const toggleFollow = () => {
        const next = !follow;
        setFollow(next);
        if (next && position && mapInstance.current) {
            const pos = { lat: Number(position.latitude ?? position.lat), lng: Number(position.longitude ?? position.lng) };
            mapInstance.current.panTo(pos);
            mapInstance.current.setZoom(18);
        }
    };

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <Box ref={mapRef} sx={{ width: '100%', height: '100%', overflow: 'hidden' }} />
            
            {/* ✅ FOCUS TOOLTIP (Title On/Off) */}
            <Tooltip title={follow ? "Follow : ON" : "Follow : OFF"} placement="right" arrow>
                <ToggleButton
                    value="check"
                    selected={follow}
                    onChange={toggleFollow}
                    sx={{
                        position: 'absolute',
                        top: 70, 
                        left: 16,
                        bgcolor: 'white',
                        borderRadius: 2,
                        boxShadow: 3,
                        '&:hover': { bgcolor: 'white' },
                        '&.Mui-selected': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'primary.dark' },
                        },
                    }}
                >
                    <LocationSearchingIcon fontSize="small" />
                </ToggleButton>
            </Tooltip>
        </Box>
    );
};

export default GoogleMap;